import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import {
  Button,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { Pencil, Trash } from 'phosphor-react'

import { Wrapper } from 'components'
import { RouteNamesAuth } from 'routes/enum'
import { Webisode } from 'api/types/webisode'
import { webisodeService } from 'api/services/webisodes'

// form validation schema
const validationSchema = Yup.object().shape({
  title: Yup.string().required('Obrigatório'),
})

export const BackOfficeWebisodes = () => {
  const navigate = useNavigate()

  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isApiBeingCalled, setIsApiBeingCalled] = useState<boolean>(false)

  // track selected campaign for deletion
  const [selected, setSelected] = useState<string>('')

  // webisode data
  const [data, setData] = useState<Webisode[] | null>(null)

  //modals visibility
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false)

  // fetch webisode list from API
  const getWebisodeList = async () => {
    setIsLoading(true)
    try {
      const response = await webisodeService.getWebisodesList()
      setData(response as Webisode[])
      setIsLoading(false)
    } catch (error) {
      toast.error('Não foi possível carregar os webisodes.')
    }
  }

  // delete selected webisode
  const deleteWebisode = async () => {
    if (!selected) return
    try {
      setIsApiBeingCalled(true)
      await webisodeService.deleteWebisode({
        id: selected,
      })
      setShowDeleteModal(false)
      getWebisodeList()
      setIsApiBeingCalled(false)
      toast.success('Webisode apagado com sucesso.')
    } catch (error) {
      toast.error('Não foi possível apagar o webisode.')
    }
  }

  // formik for create webisode form
  const formik = useFormik({
    initialValues: {
      title: '',
    },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async () => {
      setIsApiBeingCalled(true)

      try {
        const newWebisode: Webisode = {
          title: formik.values.title,
        }
        const response = await webisodeService.saveWebisode(newWebisode)

        try {
          const webisodeId = JSON.parse(response as string).id
          setShowCreateModal(false)
          setIsApiBeingCalled(false)
          navigate(
            RouteNamesAuth.BACKOFFICE_WEBISODE_DETAILS.replace(
              ':id',
              webisodeId,
            ),
          )
        } catch (error) {
          console.error('Error parsing webisode ID:', error)
        }

        toast.success('Webisode criado com sucesso.')
      } catch (error) {
        toast.error('Não foi possível criar o webisode.')
      }
    },
  })

  // initial data fetch
  useEffect(() => {
    getWebisodeList()
  }, [])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600 flex items-center justify-between">
          <span>Webisodes</span>
          <Button
            variant="default"
            color="secondary"
            onClick={() => {
              setShowCreateModal(true)
            }}
          >
            Adicionar Novo Webisode
          </Button>
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Webisode</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data &&
                  data.map((webisode: Webisode, index: number) => (
                    <TableRow
                      key={`${webisode.id}-${index}`}
                      className="even:bg-primary-50/25"
                    >
                      <TableCell className="normal-case">
                        {webisode.title}
                      </TableCell>
                      <TableCell className="normal-case">
                        {webisode.description}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="default"
                            color="secondary"
                            onClick={() => {
                              navigate(
                                RouteNamesAuth.BACKOFFICE_WEBISODE_DETAILS.replace(
                                  ':id',
                                  webisode.id!.toString(),
                                ),
                              )
                            }}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="default"
                            color="error"
                            disabled={isApiBeingCalled}
                            onClick={() => {
                              setSelected(webisode.id!.toString())
                              setShowDeleteModal(true)
                            }}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>

      <Modal open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Apagar Campanha</ModalTitle>
            <ModalDescription>
              Quer apagar esta campanha?
              <br />
              <br />
              <strong className="text-error-500">
                Todos os dados associados a esta campanha serão perdidos.
              </strong>
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" onClick={() => deleteWebisode()}>
              Apagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal open={showCreateModal} onOpenChange={setShowCreateModal}>
        <ModalContent>
          <ModalHeader className="mb-6 space-y-3">
            <ModalTitle>Criar Campanha</ModalTitle>
            <ModalDescription>
              <div className="space-y-4">
                <div className="grid grid-cols-[1fr_4fr] items-center gap-4">
                  <h3 className="text-body-4 font-bold text-metal-900">
                    Título
                  </h3>
                  <div>
                    <Input
                      name="title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>
              </div>
              <br />
              <br />
            </ModalDescription>
          </ModalHeader>
          <ModalFooter className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              color="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="secondary"
              onClick={() => formik.handleSubmit()}
            >
              Criar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
